import React, { useState, useEffect, useMemo } from 'react';
import { GripVertical, X, Plus } from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
  useDroppable, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, sortableKeyboardCoordinates,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { WIDGET_REGISTRY, getWidgetDefinition } from './widgets/widgetRegistry';

// Left/center/right, in this fixed order. Center is wider — it's the
// visual anchor (identity card lives there by default). Column widths only
// apply at the lg breakpoint; below that everything is one flattened column.
const COLUMNS = ['left', 'center', 'right'];
const COLUMN_TEMPLATE = '1fr 1.6fr 1fr';

const groupByColumn = (widgets) => {
  const groups = { left: [], center: [], right: [] };
  widgets.forEach((w) => {
    const col = COLUMNS.includes(w.column_slot) ? w.column_slot : 'center';
    groups[col].push(w);
  });
  COLUMNS.forEach((c) => groups[c].sort((a, b) => (a.position ?? 0) - (b.position ?? 0)));
  return groups;
};

const flattenColumns = (groups) =>
  COLUMNS.flatMap((col) => groups[col].map((w, i) => ({ ...w, column_slot: col, position: i })));

const WidgetSlot = ({ widget, editMode, widgetProps, dragHandleProps, isDragging, onUpdateWidgetConfig, onRemoveWidget }) => {
  const def = getWidgetDefinition(widget.widget_type);
  if (!def) return null;
  const Widget = def.component;

  return (
    <div
      className={`relative ${isDragging ? 'opacity-40' : ''}`}
      data-testid={`profile-widget-${widget.widget_type}`}
    >
      {editMode && (
        <div
          {...dragHandleProps}
          className="absolute -left-9 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-white/20 backdrop-blur-md cursor-grab active:cursor-grabbing touch-none hidden sm:flex items-center justify-center"
          aria-label={`Drag to reposition ${def.label}`}
          data-testid={`widget-drag-handle-${widget.widget_type}`}
        >
          <GripVertical className="w-4 h-4 text-white" aria-hidden="true" />
        </div>
      )}
      {editMode && def.removable && (
        <button
          onClick={() => onRemoveWidget(widget.id)}
          className="absolute -right-2 -top-2 z-10 p-1 rounded-full bg-red-500 text-white shadow-lg hover:bg-red-600 transition-colors"
          aria-label={`Remove ${def.label}`}
          data-testid={`widget-remove-${widget.widget_type}`}
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      )}
      <Widget
        {...widgetProps}
        config={widget.config}
        editMode={editMode}
        onUpdateConfig={(newConfig) => onUpdateWidgetConfig(widget.id, newConfig)}
      />
    </div>
  );
};

const SortableWidget = (props) => {
  const { widget } = props;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: widget.id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}>
      <WidgetSlot {...props} dragHandleProps={{ ...attributes, ...listeners }} isDragging={isDragging} />
    </div>
  );
};

/** A column in edit mode — a sortable list that's also its own droppable
 * target, so a column with no widgets can still receive a drop. */
const EditableColumn = ({ column, items, ...slotProps }) => {
  const { setNodeRef } = useDroppable({ id: column });
  return (
    <div ref={setNodeRef} className="flex flex-col gap-6 min-h-[80px]">
      <SortableContext items={items.map((w) => w.id)} strategy={verticalListSortingStrategy}>
        {items.map((widget) => (
          <SortableWidget key={widget.id} widget={widget} editMode {...slotProps} />
        ))}
        {items.length === 0 && (
          <div className="h-16 rounded-arch-lg border-2 border-dashed border-white/20 flex items-center justify-center text-white/40 text-[11px]">
            Drop here
          </div>
        )}
      </SortableContext>
    </div>
  );
};

const AddWidgetPicker = ({ onAdd }) => {
  const [open, setOpen] = useState(false);
  const addable = Object.entries(WIDGET_REGISTRY).filter(([, def]) => def.removable);
  if (addable.length === 0) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium bg-white/20 backdrop-blur-md text-white hover:bg-white/30 transition-colors"
        data-testid="add-widget-btn"
      >
        <Plus className="w-3.5 h-3.5" aria-hidden="true" /> Add Widget
      </button>
      {open && (
        <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-48 bg-white dark:bg-gray-800 rounded-arch-lg shadow-xl border border-white/10 overflow-hidden z-30">
          {addable.map(([type, def]) => (
            <button
              key={type}
              onClick={() => { onAdd(type); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 text-sm text-text-main hover:bg-primary/10 transition-colors"
              data-testid={`add-widget-option-${type}`}
            >
              {def.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Renders a member's profile widget layout.
 *
 * Read-only (visitors, or the owner outside edit mode): a single flattened
 * column (left, then center, then right widgets) below the lg breakpoint,
 * a real 3-column layout at lg and above.
 *
 * Edit mode (entered from the pencil on the identity card; one stacked column below lg):
 * three independently sortable columns, with drag-and-drop both within and
 * between them.
 */
const ProfileWidgetCanvas = ({ widgets, editMode, onReorder, onUpdateWidgetConfig, onAddWidget, onRemoveWidget, widgetProps }) => {
  const groups = useMemo(() => groupByColumn(widgets), [widgets]);
  const [localGroups, setLocalGroups] = useState(groups);
  const [activeWidget, setActiveWidget] = useState(null);

  useEffect(() => { setLocalGroups(groups); }, [widgets]); // eslint-disable-line react-hooks/exhaustive-deps

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  if (!editMode) {
    return (
      <>
        {/* Mobile / narrow: one flattened column (left, then center, then right) */}
        <div className="flex flex-col items-center gap-6 lg:hidden">
          {flattenColumns(groups).map((w) => (
            <WidgetSlot key={w.id} widget={w} editMode={false} widgetProps={widgetProps} />
          ))}
        </div>
        {/* Desktop: real 3 columns */}
        <div className="hidden lg:grid gap-6 items-start w-full" style={{ gridTemplateColumns: COLUMN_TEMPLATE }}>
          {COLUMNS.map((col) => (
            <div key={col} className="flex flex-col gap-6">
              {groups[col].map((w) => (
                <WidgetSlot key={w.id} widget={w} editMode={false} widgetProps={widgetProps} />
              ))}
            </div>
          ))}
        </div>
      </>
    );
  }

  const findColumnOf = (id) => COLUMNS.find((col) => localGroups[col].some((w) => w.id === id));

  const handleDragStart = (event) => {
    const col = findColumnOf(event.active.id);
    if (col) setActiveWidget(localGroups[col].find((w) => w.id === event.active.id));
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over) return;
    const activeCol = findColumnOf(active.id);
    const overCol = COLUMNS.includes(over.id) ? over.id : findColumnOf(over.id);
    if (!activeCol || !overCol || activeCol === overCol) return;

    setLocalGroups((prev) => {
      const activeItems = [...prev[activeCol]];
      const activeIndex = activeItems.findIndex((w) => w.id === active.id);
      if (activeIndex === -1) return prev;
      const [moved] = activeItems.splice(activeIndex, 1);
      const overItems = [...prev[overCol]];
      const overIndex = overItems.findIndex((w) => w.id === over.id);
      overItems.splice(overIndex >= 0 ? overIndex : overItems.length, 0, moved);
      return { ...prev, [activeCol]: activeItems, [overCol]: overItems };
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveWidget(null);
    if (!over) { onReorder(flattenColumns(localGroups)); return; }

    const activeCol = findColumnOf(active.id);
    const overCol = COLUMNS.includes(over.id) ? over.id : findColumnOf(over.id);

    let finalGroups = localGroups;
    if (activeCol && overCol && activeCol === overCol) {
      const items = localGroups[activeCol];
      const oldIndex = items.findIndex((w) => w.id === active.id);
      const newIndex = items.findIndex((w) => w.id === over.id);
      if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
        finalGroups = { ...localGroups, [activeCol]: arrayMove(items, oldIndex, newIndex) };
        setLocalGroups(finalGroups);
      }
    }
    onReorder(flattenColumns(finalGroups));
  };

  const slotProps = { widgetProps, onUpdateWidgetConfig, onRemoveWidget };
  const activeDef = activeWidget ? getWidgetDefinition(activeWidget.widget_type) : null;

  return (
    <div className="flex flex-col items-center gap-6">
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={() => { setLocalGroups(groups); setActiveWidget(null); }}
      >
        <div className="grid gap-6 items-start w-full grid-cols-1 lg:grid-cols-[1fr_1.6fr_1fr]">
          {COLUMNS.map((col) => (
            <EditableColumn key={col} column={col} items={localGroups[col]} {...slotProps} />
          ))}
        </div>
        <DragOverlay>
          {activeWidget && activeDef ? (
            <div className="opacity-90">
              <activeDef.component {...widgetProps} config={activeWidget.config} editMode={false} onUpdateConfig={() => {}} />
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
      <AddWidgetPicker onAdd={onAddWidget} />
    </div>
  );
};

export default ProfileWidgetCanvas;
